import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X } from 'lucide-react'
import axios from '../../../config/axios'
import { Step } from '../../../pages/ClientPanel/Pipelines/types'
import ApiService from '../../../services/ApiService'

interface PropsInterface {
  closeModal: () => void
  pipelineId: number
  moduleType?: string | null
}

interface SectorOption {
  id: number
  name: string
}

interface PipelineOption {
  id: number
  name: string
}

const HeaderTitleWithMode = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: 1rem;
  margin-right: 1rem;
`

const HeaderModeToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderModeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const HeaderModeSwitch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const HeaderModeThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const Select = styled.select`
  width: 100%;
  border: none;
  outline: none;
  background-color: #EBEBEB;
  height: 2.98rem;
  padding: 0 1rem;
  border-radius: 2rem;
  font-size: 15px;
  cursor: pointer;
`

const AddStepInPipeline:React.FC<PropsInterface> = ({closeModal, pipelineId, moduleType}) => {
  const [stepName, setStepName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [sectorQuery, setSectorQuery] = useState('')
  const [sectorOptions, setSectorOptions] = useState<SectorOption[]>([])
  const [selectedSector, setSelectedSector] = useState<string>('')
  const [showSectorDropdown, setShowSectorDropdown] = useState(false)
  const [moduleAreaId, setModuleAreaId] = useState<number | null>(null)
  const [isExternal, setIsExternal] = useState(false)
  const [externalPipelines, setExternalPipelines] = useState<PipelineOption[]>([])
  const [externalSteps, setExternalSteps] = useState<Step[]>([])
  const [selectedExternalPipelineId, setSelectedExternalPipelineId] = useState<number | null>(null)
  const [selectedExternalStepId, setSelectedExternalStepId] = useState<number | null>(null)
  const [usedExternalStepIds, setUsedExternalStepIds] = useState<number[]>([])
  const sectorInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const requiresSector = moduleType === 'sales' || moduleType === 'product_development'
  const moduleAreaName = moduleType === 'sales' ? 'Vendas' : 'Desenvolvimento de Produto'

  useEffect(() => {
    if (!requiresSector) return
    const fetchModuleArea = async () => {
      try {
        const response = await ApiService.services.getAreas({ query: moduleAreaName, limit: 20, offset: 0 })
        const areas = response.data?.data || response.data || []
        const matchedArea = (Array.isArray(areas) ? areas : []).find(
          (area: { id: number; name: string }) => area.name?.toLowerCase() === moduleAreaName.toLowerCase()
        )
        setModuleAreaId(matchedArea?.id ?? null)
      } catch {
        setModuleAreaId(null)
      }
    }
    fetchModuleArea()
  }, [requiresSector, moduleAreaName])

  useEffect(() => {
    if (!requiresSector || !moduleAreaId) {
      setSectorOptions([])
      return
    }
    const fetchSectors = async () => {
      try {
        const response = await ApiService.services.getSectors({ query: sectorQuery, limit: 20, offset: 0, areaId: moduleAreaId })
        const sectors = response.data?.data || response.data || []
        setSectorOptions(Array.isArray(sectors) ? sectors : [])
      } catch {
        setSectorOptions([])
      }
    }
    const debounce = setTimeout(fetchSectors, 300)
    return () => clearTimeout(debounce)
  }, [sectorQuery, requiresSector, moduleAreaId])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          sectorInputRef.current && !sectorInputRef.current.contains(e.target as Node)) {
        setShowSectorDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isExternal) return
    const fetchPipelines = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`)
        const pipelines = (response.data?.data || []).filter((p: PipelineOption) => p.id !== pipelineId)
        setExternalPipelines(pipelines)
      } catch {
        setExternalPipelines([])
      }
    }
    const fetchUsedExternalSteps = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`)
        const currentSteps: Step[] = response.data?.data || []
        setUsedExternalStepIds(
          currentSteps
            .filter((s) => s.stepType === 'external' && s.externalStepId)
            .map((s) => s.externalStepId as number)
        )
      } catch {
        setUsedExternalStepIds([])
      }
    }
    fetchPipelines()
    fetchUsedExternalSteps()
  }, [isExternal, pipelineId])

  useEffect(() => {
    setSelectedExternalStepId(null)
    if (!selectedExternalPipelineId) {
      setExternalSteps([])
      return
    }
    const fetchSteps = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${selectedExternalPipelineId}`)
        const steps = (response.data?.data || []).filter((s: Step) => s.stepType !== 'external' && !usedExternalStepIds.includes(s.id))
        setExternalSteps(steps)
      } catch {
        setExternalSteps([])
      }
    }
    fetchSteps()
  }, [selectedExternalPipelineId, usedExternalStepIds])

  const handleSaveStep = async () => {
    if (isSaving) return

    if (isExternal) {
      if (!selectedExternalPipelineId || !selectedExternalStepId) return
      const selectedStep = externalSteps.find(s => s.id === selectedExternalStepId)
      if (!selectedStep) return
      try {
        setIsSaving(true)
        const payload = {
          name: selectedStep.name,
          pipelineId,
          stepType: 'external',
          externalPipelineId: selectedExternalPipelineId,
          externalStepId: selectedExternalStepId,
        }
        const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, payload)
        if(response.data) {
          closeModal()
        }
      } catch(error) {
        console.error(error)
      } finally {
        setIsSaving(false)
      }
      return
    }

    const normalizedStepName = stepName.trim()
    const normalizedSector = selectedSector.trim()
    if (!normalizedStepName) return
    if (requiresSector && !normalizedSector) return

    try {
      setIsSaving(true)
      const payload: any = { name: normalizedStepName, pipelineId }
      if (requiresSector && normalizedSector) {
        payload.sectorName = normalizedSector
      }
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, payload)
      if(response.data) {
        closeModal()
      }
    } catch(error) {
      console.error(error) 
    } finally {
      setIsSaving(false)
    }
  }

  const handleSectorSelect = (sector: SectorOption) => {
    setSelectedSector(sector.name)
    setSectorQuery(sector.name)
    setShowSectorDropdown(false)
  }

  const handleSectorInputChange = (value: string) => {
    setSectorQuery(value)
    setSelectedSector(value)
    setShowSectorDropdown(true)
  }

  const isSaveDisabled = isSaving || (isExternal
    ? (!selectedExternalPipelineId || !selectedExternalStepId)
    : (!stepName.trim() || (requiresSector && !selectedSector.trim())))
  
  return (
    <ModalTest>
      <S.ContainerOfModal>
      <S.Header>
        <HeaderTitleWithMode>
          <S.HeaderTitle>{isExternal ? 'Adicionar etapa externa' : 'Adicionar etapa'}</S.HeaderTitle>
          <HeaderModeToggleWrapper>
            <HeaderModeLabel>Externa</HeaderModeLabel>
            <HeaderModeSwitch active={isExternal} onClick={() => setIsExternal(prev => !prev)}>
              <HeaderModeThumb active={isExternal} />
            </HeaderModeSwitch>
          </HeaderModeToggleWrapper>
        </HeaderTitleWithMode>
          <S.CloseDiv onClick={() => closeModal()}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>
        <S.FormContainer
          onSubmit={(e) => {
            e.preventDefault()
            handleSaveStep()
          }}
        >
          {!isExternal && (
            <S.InputDiv>
              <S.Label>Nome da etapa</S.Label>
              <S.Input
                placeholder='Digite aqui...'
                autoFocus
                value={stepName}
                onChange={(e) => setStepName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSaveStep()
                  }
                }}
              />
            </S.InputDiv>
          )}
          {isExternal && (
            <>
              <S.InputDiv>
                <S.Label>Funil</S.Label>
                <Select
                  value={selectedExternalPipelineId ?? ''}
                  onChange={(e) => setSelectedExternalPipelineId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value=''>Selecione o funil...</option>
                  {externalPipelines.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>{pipeline.name}</option>
                  ))}
                </Select>
              </S.InputDiv>
              <S.InputDiv>
                <S.Label>Etapa Inicial</S.Label>
                <Select
                  value={selectedExternalStepId ?? ''}
                  onChange={(e) => setSelectedExternalStepId(e.target.value ? Number(e.target.value) : null)}
                  disabled={!selectedExternalPipelineId}
                  title="Selecione a etapa que o card irá iniciar..."
                >
                  <option value=''>Selecione a etapa que o card irá iniciar...</option>
                  {externalSteps.map((step) => (
                    <option key={step.id} value={step.id}>{step.name}</option>
                  ))}
                </Select>
              </S.InputDiv>
            </>
          )}
          {!isExternal && requiresSector && (
            <S.InputDiv style={{ position: 'relative' }}>
              <S.Label>Setor</S.Label>
              <S.Input
                ref={sectorInputRef}
                placeholder='Buscar ou criar setor...'
                value={sectorQuery}
                onChange={(e) => handleSectorInputChange(e.target.value)}
                onFocus={() => setShowSectorDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSaveStep()
                  }
                }}
              />
              {showSectorDropdown && (sectorQuery.trim() || sectorOptions.length > 0) && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    maxHeight: '160px',
                    overflowY: 'auto',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  {sectorOptions.filter(s => s.name.toLowerCase().includes(sectorQuery.toLowerCase())).map((sector) => (
                    <div
                      key={sector.id}
                      onClick={() => handleSectorSelect(sector)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                    >
                      {sector.name}
                    </div>
                  ))}
                  {sectorQuery.trim() && sectorOptions.filter(s => s.name.toLowerCase().includes(sectorQuery.toLowerCase())).length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>
                      &quot;{sectorQuery}&quot; será criado automaticamente
                    </div>
                  )}
                </div>
              )}
            </S.InputDiv>
          )}
        </S.FormContainer>
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button type='button' onClick={() => closeModal()} className='cancel'>Cancelar</S.Button>
            <S.Button type='button' onClick={() => handleSaveStep()} disabled={isSaveDisabled}>Salvar</S.Button>
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

export default AddStepInPipeline